import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import toast from 'react-hot-toast'
import { PrimaryButton } from 'shared/components/button'
import FormInput from 'shared/components/form-input'
import Modal from 'shared/components/modal'
import RadioGroup from 'shared/components/radio-group'
import { AccessDenied } from 'shared/errors/access-denied'
import { ConflictError } from 'shared/errors/conflict-error'
import { NotFound } from 'shared/errors/not-found'
import { UnprocessableContentError } from 'shared/errors/unprocessable-content-error'
import {
  REPORT_EXPLANATION_MAX_LENGTH,
  REPORT_REASON_VALUES,
} from 'modules/community/const/report-reasons'
import type { ReportReason, ReportRequest } from 'modules/community/types/moderation'

export type ReportContentTargetType = 'post' | 'comment'

interface ReportContentModalProps {
  opened: boolean
  onClose: () => void
  targetType: ReportContentTargetType
  isSubmitting: boolean
  onSubmit: (body: ReportRequest) => Promise<void>
}

export const ReportContentModal = ({
  opened,
  onClose,
  targetType,
  isSubmitting,
  onSubmit,
}: ReportContentModalProps): ReactNode => {
  const { t } = useTranslation()
  const [reason, setReason] = useState<ReportReason | ''>('')
  const [explanation, setExplanation] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!opened) return
    setReason('')
    setExplanation('')
    setError(null)
  }, [opened])

  const resetAndClose = (): void => {
    if (isSubmitting) return
    onClose()
  }

  const handleSubmit = async (): Promise<void> => {
    setError(null)

    if (!reason) return

    const explanationTrimmed = explanation.trim()
    if (reason === 'other' && !explanationTrimmed) {
      setError(t('community.report.modal.explanation_required'))
      return
    }

    try {
      await onSubmit({
        reason,
        explanation: explanationTrimmed || null,
      })
      toast.success(t('community.report.success'))
      onClose()
    } catch (error_) {
      if (error_ instanceof ConflictError) {
        toast.error(t('community.report.modal.already_reported'))
        return
      }
      if (error_ instanceof AccessDenied) {
        toast.error(t('community.report.modal.forbidden'))
        return
      }
      if (error_ instanceof NotFound) {
        toast.error(t('community.report.modal.not_found'))
        return
      }
      toast.error(
        error_ instanceof UnprocessableContentError
          ? t('community.report.modal.validation_error')
          : t('core.error.internal_error_message'),
      )
    }
  }

  return (
    <Modal
      contentClassName="lg:pt-6"
      opened={opened}
      small
      isCentered
      onClose={resetAndClose}
      title={
        targetType === 'post'
          ? t('community.report.modal.title_post')
          : t('community.report.modal.title_comment')
      }
      isFetching={isSubmitting}
    >
      <div className="flex flex-col gap-6">
        <p className="text-sm text-text-primary">
          {targetType === 'post'
            ? t('community.report.modal.description_post')
            : t('community.report.modal.description_comment')}
        </p>

        <RadioGroup
          optionsWrapperClassName="mb-2 last:mb-0"
          selected={reason}
          disabled={isSubmitting}
          onChange={value => {
            setReason(value as ReportReason | '')
            setError(null)
          }}
          options={REPORT_REASON_VALUES.map(value => ({
            value,
            label: t(`community.report.reasons.${value}`),
          }))}
        />

        <FormInput
          placeholder={t('comments.components.comments.input_placeholder_label')}
          value={explanation}
          maxLength={REPORT_EXPLANATION_MAX_LENGTH}
          disabled={isSubmitting}
          error={error ?? undefined}
          inputClassName="mt-0 bg-background-primary p-4"
          onChange={event => {
            setExplanation(event.target.value)
            setError(null)
          }}
        />

        <PrimaryButton
          type="button"
          className="w-full"
          disabled={!reason || isSubmitting}
          isLoading={isSubmitting}
          onClick={() => {
            void handleSubmit()
          }}
        >
          {t('comments.components.comments.send_label')}
        </PrimaryButton>
      </div>
    </Modal>
  )
}
